import { Option } from "effect";
import { defineView } from "foldkit/submodel";

import type { Shared } from "../shared";
import { Message } from "./message";
import type { Model } from "./model";

const pad = (seconds: number) =>
	`${Math.floor(seconds / 60)}:${(seconds % 60).toString().padStart(2, "0")}`;

const formatTime = (iso: string) =>
	new Date(iso).toLocaleTimeString([], {
		hour: "2-digit",
		minute: "2-digit",
		second: "2-digit",
	});

/** v1 `AI.tsx`: one toggle button, an error banner, the countdown, and the command history (strings hardcoded in v1). */
export const view = defineView<Model, Message, Shared>((model, _shared, h) => {
	const active = Option.exists(model.session, (s) =>
		Option.isSome(s.startedAt),
	);
	const connecting = Option.isSome(model.session) && !active;
	const label = connecting
		? "Connecting..."
		: active
			? "Disconnect AI"
			: "Start AI";
	return h.div(
		[
			h.DataAttribute("testid", "page-AI"),
			h.Class("p-4 flex flex-col items-center"),
		],
		[
			h.h1([h.Class("text-2xl font-bold mb-4")], ["AI Assistant"]),
			...Option.match(model.error, {
				onNone: () => [],
				onSome: (error) => [
					h.div(
						[
							h.Role("alert"),
							h.Class("alert alert-error mb-4 w-full max-w-md"),
						],
						[h.p([], [error])],
					),
				],
			}),
			h.div(
				[h.Class("flex flex-col items-center justify-center h-64 w-full mb-8")],
				[
					h.button(
						[
							h.Type("button"),
							h.AriaLabel(label),
							h.Disabled(connecting),
							h.AriaBusy(connecting),
							h.OnClick(Message.ClickedToggle()),
							h.Class(
								`w-36 h-36 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 ease-in-out ${
									active
										? "bg-success hover:bg-error animate-pulse"
										: "bg-primary hover:bg-primary/90"
								}${connecting ? " opacity-70" : ""}`,
							),
						],
						[
							h.span(
								[h.Class("text-primary-content font-semibold text-xl")],
								[
									...(connecting
										? [
												h.svg(
													[
														h.Class(
															"animate-spin -ml-1 mr-2 h-5 w-5 text-primary-content inline",
														),
														h.Xmlns("http://www.w3.org/2000/svg"),
														h.Fill("none"),
														h.ViewBox("0 0 24 24"),
													],
													[
														h.title([], ["Loading"]),
														h.circle(
															[
																h.Class("opacity-25"),
																h.Cx("12"),
																h.Cy("12"),
																h.R("10"),
																h.Stroke("currentColor"),
																h.StrokeWidth("4"),
															],
															[],
														),
														h.path(
															[
																h.Class("opacity-75"),
																h.Fill("currentColor"),
																h.D(
																	"M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z",
																),
															],
															[],
														),
													],
												),
											]
										: []),
									label,
								],
							),
						],
					),
					...(active
						? [
								h.div(
									[h.Class("flex flex-col items-center mt-4")],
									[
										h.p(
											[h.Class("text-sm text-base-content/70 mb-1")],
											["AI assistant is active and ready to help"],
										),
										h.p(
											[h.Class("text-sm font-medium")],
											[`Time remaining: ${pad(model.remaining)}`],
										),
									],
								),
							]
						: []),
				],
			),
			...(model.history.length > 0
				? [
						h.div(
							[h.Class("w-full max-w-4xl mt-8")],
							[
								h.h2(
									[h.Class("text-xl font-semibold mb-3")],
									["Command History"],
								),
								h.ul(
									[h.Class("timeline timeline-vertical")],
									model.history.map((event, index) =>
										h.li(
											[],
											[
												index > 0 ? h.hr([]) : h.empty,
												h.div(
													[h.Class("timeline-middle")],
													[
														h.svg(
															[
																h.Xmlns("http://www.w3.org/2000/svg"),
																h.ViewBox("0 0 20 20"),
																h.Fill(
																	event.success
																		? "currentColor"
																		: "var(--color-error)",
																),
																h.Class("h-5 w-5"),
															],
															[
																h.title(
																	[],
																	[event.success ? "Success" : "Error"],
																),
																h.path(
																	[
																		h.FillRule("evenodd"),
																		h.ClipRule("evenodd"),
																		h.D(
																			event.success
																				? "M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
																				: "M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z",
																		),
																	],
																	[],
																),
															],
														),
													],
												),
												h.div(
													[h.Class("timeline-end timeline-box")],
													[
														h.p([h.Class("font-semibold")], [event.name]),
														h.p(
															[h.Class("text-sm text-base-content/70")],
															[formatTime(event.timestamp)],
														),
														h.p([h.Class("text-sm")], [event.details]),
													],
												),
												index < model.history.length - 1 ? h.hr([]) : h.empty,
											],
										),
									),
								),
							],
						),
					]
				: []),
		],
	);
});
